'use client';

import { useInfiniteScroll } from 'ahooks';
import { type MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useFetch } from '@/app/hooks';
import type { ElementType } from '@/app/types';
import { shouldPollCharacterElementTask, shouldRefreshCharacterElements } from '../_utils';
import { materialsListElementTaskHistory } from '../apis';
import type { CharacterElementTaskHistory, CharacterElementTaskStateReply } from '../types';

const PAGE_SIZE = 20;

type CharacterElementHistoryData = {
  list: CharacterElementTaskHistory[];
  nextId: string | undefined;
};

const mapStatusReplyToHistoryTask = (
  stateReply: CharacterElementTaskStateReply,
  previousTask?: CharacterElementTaskHistory,
): CharacterElementTaskHistory => ({
  ...previousTask,
  id: stateReply.task_id || previousTask?.id,
  state: stateReply.state,
  sample_count: stateReply.items?.length || previousTask?.sample_count,
  completed_count:
    stateReply.items?.filter(
      (item: NonNullable<CharacterElementTaskStateReply['items']>[number]) => item.state === 'success',
    ).length ?? previousTask?.completed_count,
  err_code: stateReply.err_code || previousTask?.err_code,
  err_msg: stateReply.err_msg || previousTask?.err_msg,
  items:
    stateReply.items?.map((item: NonNullable<CharacterElementTaskStateReply['items']>[number], index: number) => ({
      index: item.index ?? index,
      state: item.state,
      element_id: item.element_id,
      err_code: item.err_code,
      err_msg: item.err_msg,
    })) || previousTask?.items,
});

export const useCharacterElements = (target: MutableRefObject<HTMLElement | null>) => {
  const refreshedTaskIdsRef = useRef<Set<string>>(new Set());
  const [localTasks, setLocalTasks] = useState<CharacterElementTaskHistory[]>([]);

  const getHistoryList = useCallback(async (nextId?: string): Promise<CharacterElementHistoryData> => {
    const { data } = await materialsListElementTaskHistory({
      query: {
        'pager.page_token': nextId,
        'pager.pagesz': PAGE_SIZE,
        'pager.order_by': 'created_at',
        'pager.direction': 'backward',
      },
    });

    return {
      list: data?.tasks || [],
      nextId: data?.next_page_token,
    };
  }, []);

  const {
    data,
    loading: firstLoading,
    loadingMore,
    reload,
    loadMore,
    noMore,
  } = useInfiniteScroll<CharacterElementHistoryData>((previousData) => getHistoryList(previousData?.nextId), {
    target,
    isNoMore: (data) => !data?.nextId,
  });

  const { run: refresh, loading: refreshLoading } = useFetch(
    async () => {
      await reload();
      setLocalTasks([]);
    },
    {
      manual: true,
    },
  );

  const refreshElements = useCallback(() => {
    refresh();
  }, [refresh]);

  const tasks = useMemo(() => {
    const historyTasks = data?.list || [];
    const historyIds = new Set(historyTasks.map((task) => task.id).filter(Boolean));
    const localTasksById = new Map(localTasks.map((task) => [task.id, task]));
    const localOnlyTasks = localTasks.filter((task) => task.id && !historyIds.has(task.id));

    return [
      ...localOnlyTasks,
      ...historyTasks.map((task) => {
        const localTask = task.id ? localTasksById.get(task.id) : undefined;
        return localTask ? { ...task, ...localTask } : task;
      }),
    ];
  }, [data?.list, localTasks]);
  const tasksRef = useRef<CharacterElementTaskHistory[]>(tasks);
  tasksRef.current = tasks;

  const addTask = useCallback((task: CharacterElementTaskHistory) => {
    if (!task.id) return;
    setLocalTasks((currentTasks) => [task, ...currentTasks.filter((item) => item.id !== task.id)]);
  }, []);

  const handleTaskState = useCallback(
    (stateReply: CharacterElementTaskStateReply) => {
      if (!stateReply.task_id) return;

      setLocalTasks((currentTasks) => {
        const previousTask = currentTasks.find((task) => task.id === stateReply.task_id);
        const nextTask = mapStatusReplyToHistoryTask(stateReply, previousTask);

        if (!previousTask) {
          return [...currentTasks, nextTask];
        }

        return currentTasks.map((task) => (task.id === stateReply.task_id ? nextTask : task));
      });

      if (shouldRefreshCharacterElements(stateReply.state) && !refreshedTaskIdsRef.current.has(stateReply.task_id)) {
        refreshedTaskIdsRef.current.add(stateReply.task_id);
        refreshElements();
      }
    },
    [refreshElements],
  );

  const markTaskCanceled = useCallback(
    (taskId: string) => {
      setLocalTasks((currentTasks) => {
        const existingTask = tasks.find((task) => task.id === taskId);
        const canceledTask: CharacterElementTaskHistory = {
          ...existingTask,
          id: taskId,
          state: 'canceled',
          items: existingTask?.items?.map((item: NonNullable<CharacterElementTaskHistory['items']>[number]) =>
            item.element ? item : { ...item, state: 'canceled' },
          ),
        };
        return [canceledTask, ...currentTasks.filter((task) => task.id !== taskId)];
      });
      refreshElements();
    },
    [refreshElements, tasks],
  );

  const updateTaskElement = useCallback((element: ElementType) => {
    if (!element.id) return;

    const updatedTasks = tasksRef.current.flatMap((task) => {
      let changed = false;
      const items = task.items?.map((item) => {
        const itemElementId = item.element?.id || item.element_id;

        if (itemElementId !== element.id) return item;

        changed = true;
        return {
          ...item,
          element: {
            ...item.element,
            ...element,
          },
        };
      });

      return changed ? [{ ...task, items }] : [];
    });

    if (updatedTasks.length === 0) return;

    setLocalTasks((currentTasks) => {
      const updatedTasksById = new Map(updatedTasks.map((task) => [task.id, task]));
      const currentIds = new Set(currentTasks.map((task) => task.id));

      return [
        ...currentTasks.map((task) => {
          const updatedTask = task.id ? updatedTasksById.get(task.id) : undefined;
          return updatedTask || task;
        }),
        ...updatedTasks.filter((task) => task.id && !currentIds.has(task.id)),
      ];
    });
  }, []);

  const activeTaskId = useMemo(
    () => tasks.find((task) => task.id && shouldPollCharacterElementTask(task.state))?.id,
    [tasks],
  );

  return {
    tasks,
    loading: firstLoading,
    loadingMore,
    loadMore,
    noMore,
    refreshLoading,
    refreshElements,
    addTask,
    activeTaskId,
    handleTaskState,
    markTaskCanceled,
    updateTaskElement,
  };
};
